/**
 * Criar oportunidade a partir do funil (H09).
 *
 * ── Por que aqui, e não só no CRM ─────────────────────────────────────────
 *
 * O funil é onde o vendedor está quando percebe que existe um negócio: ele
 * acabou de mover um card, viu a coluna vazia, lembrou do cliente que ligou.
 * Mandá-lo abrir outro sistema para registrar é o caminho que ninguém percorre
 * — e o negócio fica sem registro, que é o que o funil existe para evitar.
 *
 * ── O mínimo, e nada além ─────────────────────────────────────────────────
 *
 * Nome é obrigatório porque é o que o card mostra. Valor e etapa são
 * opcionais, e a etapa vazia significa "a primeira do funil DA REVENDA" — quem
 * decide isso é o servidor, que conhece o funil; um valor fixo aqui quebraria
 * na primeira revenda com funil diferente.
 *
 * O cliente entrou depois, quando a busca passou a existir (D24/H17). Ele é
 * opcional de propósito: o vendedor que acabou de desligar o telefone às vezes
 * não sabe ainda quem é a pessoa no cadastro, e exigir o vínculo agora faria
 * ele fechar o formulário e não registrar o negócio — que é o contrário do que
 * esta tela existe para fazer.
 */

import { useMemo, useState, type ReactNode } from 'react';
import type { ClienteDaLista, MudancaDeCampos } from '@cliqcrm/contracts';
import { useCamposPersonalizados } from '@/shared/api/consultas';
import { CamposEditaveis, obrigatoriosVazios } from '@/entities/campos/campo-personalizado';
import { useListaDeClientes } from '@/entities/clientes/cliente';
import { Avatar, Botao, Campo, CampoSelecao, Dialogo } from '@/shared/ui';
import { NotasPendentes } from '@/features/comercial/notas-da-oportunidade';

export interface DadosDaNovaOportunidade {
  readonly nome: string;
  readonly etapa?: string | null;
  readonly valor?: { readonly quantia_micros: number; readonly moeda: string } | null;
  /** Canônico do cliente (§5.1) — nunca o id da engine. */
  readonly clienteId?: string | null;
  /**
   * O que foi combinado, escrito antes de o negócio existir.
   *
   * Viaja no MESMO pedido: criar primeiro e anotar depois deixaria a segunda
   * metade dependendo de a pessoa voltar ao card — e o combinado ficaria de
   * fora justamente quando ela ainda tem tudo fresco na cabeça.
   */
  readonly notas?: readonly string[];
  /** Os campos personalizados preenchidos ao criar (#568). */
  readonly campos?: MudancaDeCampos;
}

// O parser do valor desceu para `shared/lib` (#568): o campo personalizado de
// valor em R$ lê do mesmo jeito. Reexportado para quem já importava daqui.
export { emMicros } from '@/shared/lib/formato';
import { emMicros } from '@/shared/lib/formato';

export function DialogoDeNovaOportunidade({
  etapas,
  salvando,
  aoCriar,
  aoFechar,
}: {
  /** As etapas do funil DA REVENDA, na ordem. A primeira é o padrão. */
  etapas: readonly { readonly chave: string; readonly nome: string }[];
  salvando: boolean;
  aoCriar: (dados: DadosDaNovaOportunidade) => void;
  aoFechar: () => void;
}): ReactNode {
  const [nome, definirNome] = useState('');
  const [valor, definirValor] = useState('');
  const [etapa, definirEtapa] = useState('');
  const [cliente, definirCliente] = useState<ClienteDaLista | null>(null);
  const [notas, definirNotas] = useState<readonly string[]>([]);
  // Os campos personalizados (#568). O obrigatório "desde a criação" trava aqui;
  // o "a partir de uma etapa" o servidor confere contra a etapa escolhida.
  const campos = useCamposPersonalizados('oportunidade');
  const [mudancaDosCampos, definirMudancaDosCampos] = useState<MudancaDeCampos>({});
  const faltando = obrigatoriosVazios(campos.data?.itens ?? [], {}, mudancaDosCampos);

  const micros = emMicros(valor);
  const valorInvalido = valor.trim() !== '' && micros === null;
  const podeCriar = nome.trim() !== '' && !valorInvalido && !salvando && faltando.length === 0;

  return (
    <Dialogo
      titulo="Nova oportunidade"
      forma="lateral"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao
            onClick={aoFechar}
            desabilitado={salvando}
            motivo="Aguarde: a oportunidade está sendo criada."
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="nova-oportunidade"
            variante="primario"
            desabilitado={!podeCriar}
            motivo={
              salvando
                ? 'Criando…'
                : nome.trim() === ''
                  ? 'Preencha o nome para criar.'
                  : faltando.length > 0
                    ? `Preencha ${faltando.map((campo) => campo.nome).join(', ')}.`
                    : 'Corrija o valor para criar.'
            }
          >
            {salvando ? 'Criando…' : 'Criar oportunidade'}
          </Botao>
        </>
      }
    >
      <form
        id="nova-oportunidade"
        className="nova-op__formulario"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!podeCriar) return;
          aoCriar({
            nome: nome.trim(),
            // Vazio vira ausente, e não string vazia: o servidor lê ausência
            // como "a primeira etapa do funil", e uma string vazia seria uma
            // etapa que não existe.
            etapa: etapa === '' ? null : etapa,
            valor: micros === null ? null : { quantia_micros: micros, moeda: 'BRL' },
            clienteId: cliente?.id ?? null,
            notas,
            ...(Object.keys(mudancaDosCampos).length > 0 ? { campos: mudancaDosCampos } : {}),
          });
        }}
      >
        <Campo
          rotulo="Nome"
          type="text"
          value={nome}
          autoFocus
          maxLength={500}
          placeholder="Ex.: Maria Silva"
          onChange={(evento) => definirNome(evento.target.value)}
        />

        <Campo
          rotulo="Valor (opcional)"
          type="text"
          inputMode="decimal"
          value={valor}
          placeholder="R$ 0,00"
          aria-invalid={valorInvalido}
          onChange={(evento) => definirValor(evento.target.value)}
        />
        {valorInvalido && (
          <p className="dialogo__erro" role="alert">
            Não entendi esse valor. Escreva como você fala: 12.500,00 ou 12500.
          </p>
        )}

        <CampoSelecao
          rotulo="Etapa"
          value={etapa}
          onChange={(evento) => definirEtapa(evento.target.value)}
        >
          {/*
              A opção vazia é a PRIMEIRA e diz o que faz. Deixá-la em branco
              faria parecer que a oportunidade nasce sem etapa — e ela nasce,
              sim, na primeira do funil desta revenda.
            */}
          <option value="">Primeira etapa do funil</option>
          {etapas.map((e) => (
            <option key={e.chave} value={e.chave}>
              {e.nome}
            </option>
          ))}
        </CampoSelecao>

        <EscolherCliente escolhido={cliente} aoEscolher={definirCliente} />

        <CamposEditaveis
          id="nova-oportunidade-campo"
          campos={campos.data?.itens ?? []}
          valores={{}}
          mudanca={mudancaDosCampos}
          aoMudar={definirMudancaDosCampos}
        />
      </form>

      {/*
        FORA do `form`, e de propósito.

        O campo de nota tem botão próprio, e dentro do formulário ele viraria um
        segundo `submit` — anotar criaria a oportunidade. É a mesma razão pela
        qual a busca de cliente não usa `type="submit"` em nada.
      */}
      <NotasPendentes notas={notas} aoMudar={definirNotas} />
    </Dialogo>
  );
}

/**
 * Escolher o cliente da oportunidade, sem sair do formulário.
 *
 * ── Por que a MESMA busca da lista de clientes ────────────────────────────
 *
 * `useListaDeClientes` já resolve as três coisas difíceis desta caixa: a
 * espera antes de consultar, o descarte da resposta que chega fora de ordem e
 * a leitura do termo pelo servidor — nome, telefone, e-mail ou documento, sem
 * a pessoa precisar dizer qual é qual.
 *
 * Escrever uma busca própria aqui duplicaria as três, e a divergência
 * apareceria como "a busca do funil não acha o que a lista acha".
 *
 * ── Sem digitar nada, já mostra alguém ────────────────────────────────────
 *
 * A primeira consulta sai com o termo vazio, e a lista vem ordenada por última
 * interação. Quem acabou de desligar o telefone quase sempre encontra a pessoa
 * na primeira linha, sem digitar — e esse é o caso comum de quem cria uma
 * oportunidade logo depois de uma conversa.
 *
 * ── Poucos resultados, de propósito ───────────────────────────────────────
 *
 * Seis. Isto é um campo dentro de um formulário, não a tela de clientes: uma
 * lista longa aqui empurraria os botões para fora da vista, e quem precisa
 * procurar de verdade tem a tela inteira do outro lado do menu.
 */
/**
 * O mínimo que a caixa precisa saber de quem já está escolhido.
 *
 * Exportado com o componente porque a edição (`editar-oportunidade.tsx`) chega
 * com o cliente que vem no CARD do funil — id e nome, sem telefone. Exigir
 * `ClienteDaLista` ali obrigaria a buscar o cliente inteiro só para desenhar o
 * nome que a tela já tem.
 */
export interface EscolhaDeCliente {
  readonly id: string;
  readonly nome_exibicao: string | null;
  readonly telefone?: string | null;
}

export function EscolherCliente({
  escolhido,
  aoEscolher,
}: {
  escolhido: EscolhaDeCliente | null;
  aoEscolher: (cliente: ClienteDaLista | null) => void;
}): ReactNode {
  const [busca, definirBusca] = useState('');
  /*
   * Aqui o recorte é `todos`, e por NOME.
   *
   * Esta caixa é um seletor, não a fila de trabalho: quem está criando uma
   * oportunidade já sabe para quem, e "esfriando" esconderia justamente o
   * cliente com quem a pessoa acabou de falar. O `useMemo` é o que impede o
   * objeto novo a cada tecla de reiniciar a lista sozinho.
   */
  const filtro = useMemo(
    () => ({ busca, recorte: 'todos', ordenar: 'nome', direcao: 'asc' }) as const,
    [busca],
  );
  const lista = useListaDeClientes(filtro);

  if (escolhido) {
    return (
      <div className="nova-op__escolha">
        <span className="nova-op__escolha-rotulo">Cliente</span>
        <p className="nova-op__cliente">
          <Avatar nome={escolhido.nome_exibicao} semente={escolhido.id} tamanho={26} />
          <span className="nova-op__cliente-nome">
            {escolhido.nome_exibicao ?? 'Contato sem nome'}
          </span>
          {escolhido.telefone && (
            <span className="nova-op__cliente-contato">{escolhido.telefone}</span>
          )}
          <button type="button" onClick={() => aoEscolher(null)}>
            trocar
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="nova-op__escolha">
      {/*
        "Opcional" está no rótulo, e não numa nota embaixo.

        O vendedor que acabou de desligar o telefone às vezes não sabe ainda
        quem é a pessoa no cadastro. Se o campo parecer obrigatório, ele fecha
        o formulário e não registra o negócio — que é o contrário do que esta
        tela existe para fazer.
      */}
      <Campo
        rotulo="Cliente (opcional)"
        type="search"
        value={busca}
        placeholder="Buscar por nome, telefone, e-mail ou documento"
        onChange={(evento) => definirBusca(evento.target.value)}
      />

      {lista.itens.length > 0 && (
        <ul className="nova-op__resultados">
          {lista.itens.slice(0, 6).map((candidato) => (
            <li key={candidato.id}>
              <button type="button" onClick={() => aoEscolher(candidato)}>
                <span className="nova-op__cliente-nome">
                  {candidato.nome_exibicao ?? 'Contato sem nome'}
                </span>
                <span className="nova-op__cliente-contato">
                  {candidato.telefone ?? candidato.email ?? 'sem contato'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {busca.trim() !== '' && !lista.carregando && lista.itens.length === 0 && (
        <p className="dialogo__apoio">
          {/*
            Nada encontrado NÃO é impedimento: a oportunidade pode nascer sem
            cliente e ser vinculada depois. Dizer isso aqui evita que a pessoa
            fique procurando um cadastro que talvez ainda não exista.
          */}
          Nenhum cliente com esse termo. Você pode criar a oportunidade assim mesmo e vincular
          depois.
        </p>
      )}
    </div>
  );
}
